import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useI18n } from "../i18n/I18nContext";

const CANVAS_SIZE = 720;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const numberOr = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getPointerPosition = (event, canvas) => {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  return {
    x: (event.clientX - rect.left) * scaleX,
    y: (event.clientY - rect.top) * scaleY,
  };
};

const loadCanvasImage = (src) => (
  new Promise((resolve, reject) => {
    if (!src) {
      resolve(null);
      return;
    }
    const image = new Image();
    if (/^https?:\/\//i.test(src)) {
      image.crossOrigin = "anonymous";
    }
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  })
);

const drawImageCover = (ctx, image, x, y, width, height) => {
  const imageRatio = image.width / image.height;
  const targetRatio = width / height;
  let sourceWidth = image.width;
  let sourceHeight = image.height;
  let sourceX = 0;
  let sourceY = 0;

  if (imageRatio > targetRatio) {
    sourceWidth = image.height * targetRatio;
    sourceX = (image.width - sourceWidth) / 2;
  } else {
    sourceHeight = image.width / targetRatio;
    sourceY = (image.height - sourceHeight) / 2;
  }

  ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
};

const CustomYoYoPreview = forwardRef(({
  baseImage,
  uploadedImage,
  mockupConfig,
  initialDraft,
  onPreviewGenerated,
  onDraftChange,
}, ref) => {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const baseImageRef = useRef(null);
  const artworkImageRef = useRef(null);
  const dragRef = useRef(null);
  const drawRef = useRef(() => {});
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: CANVAS_SIZE * 0.5, y: CANVAS_SIZE * 0.5 });
  const [printArea, setPrintArea] = useState({
    x: CANVAS_SIZE * 0.5,
    y: CANVAS_SIZE * 0.5,
    radius: CANVAS_SIZE * 0.22,
  });
  const [editMode, setEditMode] = useState("artwork");
  const [isDragging, setIsDragging] = useState(false);
  const [loadError, setLoadError] = useState("");

  const config = useMemo(() => ({
    centerXRatio: Number(mockupConfig?.centerXRatio) || 0.5,
    centerYRatio: Number(mockupConfig?.centerYRatio) || 0.5,
    radiusRatio: Number(mockupConfig?.radiusRatio) || 0.22,
  }), [mockupConfig?.centerXRatio, mockupConfig?.centerYRatio, mockupConfig?.radiusRatio]);

  const defaultFace = useMemo(() => ({
    x: CANVAS_SIZE * config.centerXRatio,
    y: CANVAS_SIZE * config.centerYRatio,
    radius: CANVAS_SIZE * config.radiusRatio,
  }), [config.centerXRatio, config.centerYRatio, config.radiusRatio]);

  const face = printArea;

  const clampPrintArea = useCallback((area) => {
    const radius = clamp(numberOr(area?.radius, defaultFace.radius), 1, CANVAS_SIZE / 2);
    return {
      x: clamp(numberOr(area?.x, defaultFace.x), radius, CANVAS_SIZE - radius),
      y: clamp(numberOr(area?.y, defaultFace.y), radius, CANVAS_SIZE - radius),
      radius,
    };
  }, [defaultFace.radius, defaultFace.x, defaultFace.y]);

  const getDraft = useCallback(() => ({
    artworkTransform: {
      x: position.x,
      y: position.y,
      scale,
      rotation,
    },
    printAreaConfig: {
      centerX: printArea.x,
      centerY: printArea.y,
      radius: printArea.radius,
      centerXRatio: printArea.x / CANVAS_SIZE,
      centerYRatio: printArea.y / CANVAS_SIZE,
      radiusRatio: printArea.radius / CANVAS_SIZE,
    },
  }), [position.x, position.y, printArea.radius, printArea.x, printArea.y, rotation, scale]);

  const resetArtwork = useCallback(() => {
    setScale(1);
    setRotation(0);
    setPosition({ x: face.x, y: face.y });
  }, [face.x, face.y]);

  const resetPrintArea = useCallback(() => {
    const nextPrintArea = clampPrintArea(defaultFace);
    setPrintArea(nextPrintArea);
    setPosition({ x: nextPrintArea.x, y: nextPrintArea.y });
  }, [clampPrintArea, defaultFace]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    const background = ctx.createLinearGradient(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    background.addColorStop(0, "#f8f8f6");
    background.addColorStop(1, "#ece8e1");
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    if (baseImageRef.current) {
      drawImageCover(ctx, baseImageRef.current, 0, 0, CANVAS_SIZE, CANVAS_SIZE);
    } else {
      ctx.save();
      ctx.translate(face.x, face.y);
      ctx.beginPath();
      ctx.arc(0, 0, face.radius * 1.85, 0, Math.PI * 2);
      ctx.fillStyle = "#f4f4f1";
      ctx.fill();
      ctx.lineWidth = 24;
      ctx.strokeStyle = "#d72128";
      ctx.stroke();
      ctx.restore();
    }

    ctx.save();
    ctx.beginPath();
    ctx.arc(face.x, face.y, face.radius, 0, Math.PI * 2);
    ctx.clip();

    if (artworkImageRef.current) {
      const artwork = artworkImageRef.current;
      const baseSize = face.radius * 1.72;
      const artworkRatio = artwork.width / artwork.height;
      const drawWidth = artworkRatio >= 1 ? baseSize * scale : baseSize * artworkRatio * scale;
      const drawHeight = artworkRatio >= 1 ? (baseSize / artworkRatio) * scale : baseSize * scale;

      ctx.save();
      ctx.translate(position.x, position.y);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.globalAlpha = 0.96;
      ctx.drawImage(artwork, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
      ctx.restore();
    } else {
      ctx.fillStyle = "rgba(255, 255, 255, 0.72)";
      ctx.fillRect(face.x - face.radius, face.y - face.radius, face.radius * 2, face.radius * 2);
    }

    const surfaceShade = ctx.createRadialGradient(
      face.x - face.radius * 0.38,
      face.y - face.radius * 0.48,
      face.radius * 0.12,
      face.x,
      face.y,
      face.radius,
    );
    surfaceShade.addColorStop(0, "rgba(255, 255, 255, 0.46)");
    surfaceShade.addColorStop(0.42, "rgba(255, 255, 255, 0.04)");
    surfaceShade.addColorStop(1, "rgba(0, 0, 0, 0.28)");
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = surfaceShade;
    ctx.fillRect(face.x - face.radius, face.y - face.radius, face.radius * 2, face.radius * 2);
    ctx.restore();

    ctx.save();
    ctx.beginPath();
    ctx.arc(face.x, face.y, face.radius + 1, 0, Math.PI * 2);
    ctx.lineWidth = Math.max(2, face.radius * 0.045);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(face.x, face.y, face.radius + ctx.lineWidth, 0, Math.PI * 2);
    ctx.lineWidth = Math.max(4, face.radius * 0.08);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.18)";
    ctx.stroke();
    if (editMode === "printArea") {
      ctx.setLineDash([10, 8]);
      ctx.beginPath();
      ctx.arc(face.x, face.y, face.radius, 0, Math.PI * 2);
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(157, 31, 37, 0.9)";
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.restore();
  }, [editMode, face.radius, face.x, face.y, position.x, position.y, rotation, scale]);

  useEffect(() => {
    drawRef.current = draw;
  }, [draw]);

  useEffect(() => {
    let cancelled = false;
    setLoadError("");
    loadCanvasImage(baseImage)
      .then((image) => {
        if (!cancelled) {
          baseImageRef.current = image;
          drawRef.current();
        }
      })
      .catch(() => {
        if (!cancelled) {
          baseImageRef.current = null;
          setLoadError(t("customizer.preview.loadModelError"));
          drawRef.current();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [baseImage, defaultFace, initialDraft, t]);

  useEffect(() => {
    let cancelled = false;
    setLoadError("");
    loadCanvasImage(uploadedImage)
      .then((image) => {
        if (!cancelled) {
          artworkImageRef.current = image;
          resetArtwork();
          drawRef.current();
        }
      })
      .catch(() => {
        if (!cancelled) {
          artworkImageRef.current = null;
          setLoadError(t("customizer.preview.loadArtworkError"));
          drawRef.current();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [resetArtwork, t, uploadedImage]);

  useEffect(() => {
    draw();
  }, [draw]);

  useEffect(() => {
    if (!initialDraft) {
      const nextPrintArea = clampPrintArea(defaultFace);
      setPrintArea(nextPrintArea);
      setPosition({ x: nextPrintArea.x, y: nextPrintArea.y });
      setScale(1);
      setRotation(0);
      return;
    }
    const draftPrint = initialDraft.printAreaConfig || {};
    const draftArtwork = initialDraft.artworkTransform || {};
    const nextPrintArea = clampPrintArea({
      x: numberOr(draftPrint.centerX, numberOr(draftPrint.centerXRatio, defaultFace.x / CANVAS_SIZE) * CANVAS_SIZE),
      y: numberOr(draftPrint.centerY, numberOr(draftPrint.centerYRatio, defaultFace.y / CANVAS_SIZE) * CANVAS_SIZE),
      radius: numberOr(draftPrint.radius, numberOr(draftPrint.radiusRatio, defaultFace.radius / CANVAS_SIZE) * CANVAS_SIZE),
    });
    setPrintArea(nextPrintArea);
    setPosition({
      x: numberOr(draftArtwork.x, nextPrintArea.x),
      y: numberOr(draftArtwork.y, nextPrintArea.y),
    });
    setScale(numberOr(draftArtwork.scale, 1));
    setRotation(numberOr(draftArtwork.rotation, 0));
  }, [baseImage, clampPrintArea, defaultFace, initialDraft]);

  useEffect(() => {
    if (onDraftChange) {
      onDraftChange(getDraft());
    }
  }, [getDraft, onDraftChange]);

  const generatePreview = useCallback(() => {
    draw();
    const dataUrl = canvasRef.current?.toDataURL("image/png");
    if (dataUrl && onPreviewGenerated) {
      onPreviewGenerated(dataUrl);
    }
    return dataUrl;
  }, [draw, onPreviewGenerated]);

  useImperativeHandle(ref, () => ({
    generatePreview,
    getDraft,
    resetArtwork,
    resetPrintArea,
  }), [generatePreview, getDraft, resetArtwork, resetPrintArea]);

  const handlePointerDown = (event) => {
    if (!canvasRef.current) return;
    if (editMode === "artwork" && !artworkImageRef.current) return;
    const point = getPointerPosition(event, canvasRef.current);
    dragRef.current = {
      pointerId: event.pointerId,
      x: point.x,
      y: point.y,
      position,
      printArea,
      editMode,
    };
    canvasRef.current.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current || !canvasRef.current) return;
    const point = getPointerPosition(event, canvasRef.current);
    if (dragRef.current.editMode === "printArea") {
      const dx = point.x - dragRef.current.x;
      const dy = point.y - dragRef.current.y;
      const nextPrintArea = clampPrintArea({
        ...dragRef.current.printArea,
        x: dragRef.current.printArea.x + dx,
        y: dragRef.current.printArea.y + dy,
      });
      const appliedDx = nextPrintArea.x - dragRef.current.printArea.x;
      const appliedDy = nextPrintArea.y - dragRef.current.printArea.y;
      setPrintArea(nextPrintArea);
      setPosition({
        x: dragRef.current.position.x + appliedDx,
        y: dragRef.current.position.y + appliedDy,
      });
      return;
    }
    setPosition({
      x: dragRef.current.position.x + point.x - dragRef.current.x,
      y: dragRef.current.position.y + point.y - dragRef.current.y,
    });
  };

  const handlePointerUp = (event) => {
    canvasRef.current?.releasePointerCapture?.(event.pointerId);
    dragRef.current = null;
    setIsDragging(false);
  };

  const handleWheel = (event) => {
    if (!artworkImageRef.current) return;
    event.preventDefault();
    setScale((currentScale) => clamp(currentScale + event.deltaY * -0.0015, 0.35, 2.8));
  };

  return (
    <div className="custom-preview">
      <div className={`custom-preview-canvas-shell${isDragging ? " is-dragging" : ""}`}>
        <canvas
          ref={canvasRef}
          width={CANVAS_SIZE}
          height={CANVAS_SIZE}
          aria-label={t("customizer.preview.canvasAria")}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onWheel={handleWheel}
        />
      </div>

      {loadError && <p className="custom-preview-error">{loadError}</p>}

      <div className="custom-preview-controls">
        <div className="custom-preview-mode" role="group" aria-label={t("customizer.preview.modeAria")}>
          <button
            type="button"
            className={editMode === "artwork" ? "active" : ""}
            onClick={() => setEditMode("artwork")}
          >
            {t("customizer.preview.moveArtwork")}
          </button>
          <button
            type="button"
            className={editMode === "printArea" ? "active" : ""}
            onClick={() => setEditMode("printArea")}
          >
            {t("customizer.preview.movePrintArea")}
          </button>
        </div>
        <label>
          {t("customizer.preview.scale")}
          <input
            type="range"
            min="0.35"
            max="2.8"
            step="0.01"
            value={scale}
            disabled={!uploadedImage}
            onChange={(event) => setScale(Number(event.target.value))}
          />
        </label>
        <label>
          {t("customizer.preview.rotation")}
          <input
            type="range"
            min="-180"
            max="180"
            step="1"
            value={rotation}
            disabled={!uploadedImage}
            onChange={(event) => setRotation(Number(event.target.value))}
          />
        </label>
        <label>
          {t("customizer.preview.printAreaSize")}
          <input
            type="range"
            min="60"
            max="250"
            step="1"
            value={printArea.radius}
            onChange={(event) => setPrintArea((current) => clampPrintArea({
              ...current,
              radius: Number(event.target.value),
            }))}
          />
        </label>
      </div>

      <div className="custom-preview-actions">
        <button type="button" onClick={resetArtwork} disabled={!uploadedImage}>
          {t("customizer.preview.resetArtwork")}
        </button>
        <button type="button" onClick={resetPrintArea}>
          {t("customizer.preview.resetPrintArea")}
        </button>
        <button type="button" onClick={generatePreview} disabled={!uploadedImage}>
          {t("customizer.preview.generate")}
        </button>
      </div>
    </div>
  );
});

export default CustomYoYoPreview;
